import { MEMBERS_AREA_FEATURED_PRODUCT } from '@constants/features'
import { useCourseRouteParams } from '@contexts/CourseRouteParamsContext'
import { useAdminPreview } from '@hooks/useAdminPreview'
import { useMembersConfig } from '@hooks/useMembersConfig'
import { useThemeParams } from '@hooks/useThemeParams'
import { homePath } from '@utils/coursePaths'
import { router } from 'expo-router'
import { ReactNode, useEffect } from 'react'

interface IFeaturedCourseRedirectProps {
  children: ReactNode
  fallback: ReactNode
}

/**
 * A casa do curso principal é a home focada: a tela padrão dele (link antigo,
 * "Voltar" de aula, card) leva para `/`. `dismissTo` volta até a home que já
 * está na pilha, ou troca esta tela por ela quando não há; `replace` empilharia
 * uma segunda home no app nativo. Quem não tem acesso fica na tela do curso,
 * porque `/` mostraria a home padrão e as duas não se alternam.
 */
export function FeaturedCourseRedirect({
  children,
  fallback,
}: IFeaturedCourseRedirectProps) {
  const { courseId } = useCourseRouteParams()
  const { isPreview } = useAdminPreview()
  const { data, error, isResolved } = useMembersConfig()
  const {
    data: themeParams,
    loading: themeParamsLoading,
    error: themeParamsError,
  } = useThemeParams()

  const routeCourseId = Array.isArray(courseId) ? courseId[0] : courseId
  const featuredProduct = data?.featuredProduct
  const isFeaturedCourse =
    !error &&
    !!featuredProduct &&
    !!routeCourseId &&
    String(featuredProduct.courseId) === routeCourseId &&
    (featuredProduct.activeForUser || isPreview)

  useEffect(() => {
    if (isFeaturedCourse) router.dismissTo(homePath(isPreview))
  }, [isFeaturedCourse, isPreview])

  // Mesmo critério da home: sem a feature, a tela abre como antes da feature,
  // sem esperar a configuração.
  const featuresAvailable = themeParams?.school?.featuresAvailable
  const schoolLacksFeature =
    !themeParamsLoading &&
    !themeParamsError &&
    !!featuresAvailable &&
    !featuresAvailable.includes(MEMBERS_AREA_FEATURED_PRODUCT)
  if (schoolLacksFeature || error) return <>{children}</>

  if (!isResolved || isFeaturedCourse) return <>{fallback}</>

  return <>{children}</>
}
